import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import packagesData from "../data/packages";
import { 
  MapPin, Clock, Calendar, Users, Hotel, Utensils, Car, 
  Check, X, ChevronRight, ArrowLeft, Phone
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import BookingConfirmation from '../components/BookingConfirmation';
import BookingModal from '../components/BookingModal';


export default function PackageDetailsPage() {
  const { id } = useParams();
  const [pkg, setPkg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeDay, setActiveDay] = useState(0);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [bookingDetails, setBookingDetails] = useState(null);

  useEffect(() => {
  const foundPackage = packagesData.find(p => p.id === parseInt(id));
  setPkg(foundPackage);
  setLoading(false);
}, [id]);

  
  const handleBookingSubmit = (customerData) => {

const message =
`Hello, I want to book this package:

Package: ${pkg.name}
Destination: ${pkg.destination}
Duration: ${pkg.duration}
Price: ₹${pkg.price}

Name: ${customerData.name}
Mobile: ${customerData.mobile}`;

window.open(
`https://wa.me/919281410305?text=${encodeURIComponent(message)}`,
"_blank"
);
};

      

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="w-8 h-8 border-4 border-teal-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!pkg) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center pt-20">
        <h2 className="text-2xl font-semibold text-slate-900 mb-4">Package Not Found</h2>
        <Link to="/packages">
          <Button>Back to Packages</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pt-20" data-testid="package-details-page">
      {/* Hero Image */}
      <div className="relative h-[50vh] md:h-[60vh]">
        <img
          src={pkg.image}
          alt={pkg.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12 lg:p-24">
          <div className="max-w-7xl mx-auto">
            <Link to="/packages" className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-4 transition-colors" data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
              Back to Packages
            </Link>
            {pkg.badge && (
              <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-orange-500 text-white mb-4 ml-4">
                {pkg.badge}
              </span>
            )}
            <h1 className="text-3xl md:text-5xl font-medium text-white mb-4">{pkg.name}</h1>
            <div className="flex flex-wrap items-center gap-4 text-white/90">
              <span className="flex items-center gap-2">
                <MapPin className="w-5 h-5" />
                {pkg.destination}
              </span>
              <span className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                {pkg.duration}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Highlights */}
            <motion.section
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl p-6 md:p-8 shadow-sm"
              data-testid="highlights-section"
            >
              <h2 className="text-2xl font-semibold text-slate-900 mb-6">Package Highlights</h2>
              <div className="grid grid-cols-2 gap-4">
                {pkg.highlights?.map((highlight, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 bg-teal-50 rounded-xl">
                    <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center">
                      <Check className="w-4 h-4 text-teal-600" />
                    </div>
                    <span className="text-slate-700">{highlight}</span>
                  </div>
                ))}
              </div>
            </motion.section>

            {/* Quick Info */}
            <motion.section
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-2xl p-6 md:p-8 shadow-sm"
              data-testid="quick-info-section"
            >
              <h2 className="text-2xl font-semibold text-slate-900 mb-6">What's Included</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-slate-50 rounded-xl">
                  <Hotel className="w-8 h-8 text-teal-600 mx-auto mb-2" />
                  <p className="text-sm font-medium text-slate-900">Hotels</p>
                  <p className="text-xs text-slate-500">{pkg.hotels}</p>
                </div>
                <div className="text-center p-4 bg-slate-50 rounded-xl">
                  <Utensils className="w-8 h-8 text-teal-600 mx-auto mb-2" />
                  <p className="text-sm font-medium text-slate-900">Meals</p>
                  <p className="text-xs text-slate-500">{pkg.meals}</p>
                </div>
                <div className="text-center p-4 bg-slate-50 rounded-xl">
                  <Car className="w-8 h-8 text-teal-600 mx-auto mb-2" />
                  <p className="text-sm font-medium text-slate-900">Transport</p>
                  <p className="text-xs text-slate-500">{pkg.transport}</p>
                </div>
                <div className="text-center p-4 bg-slate-50 rounded-xl">
                  <Calendar className="w-8 h-8 text-teal-600 mx-auto mb-2" />
                  <p className="text-sm font-medium text-slate-900">Duration</p>
                  <p className="text-xs text-slate-500">{pkg.duration}</p>
                </div>
              </div>
            </motion.section>

            {/* Itinerary */}
            <motion.section
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-2xl p-6 md:p-8 shadow-sm"
              data-testid="itinerary-section"
            >
              <h2 className="text-2xl font-semibold text-slate-900 mb-6">Day-wise Itinerary</h2>
              
              {/* Day Tabs */}
              <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
                {pkg.itinerary?.map((day, index) => (
                  <button
                    key={index}
                    onClick={() => setActiveDay(index)}
                    className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                      activeDay === index
                        ? 'bg-teal-600 text-white'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                    data-testid={`day-tab-${index}`}
                  >
                    Day {day.day}
                  </button>
                ))}
              </div>

              {/* Active Day Content */}
              {pkg.itinerary?.[activeDay] && (
                <motion.div
                  key={activeDay}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="bg-gradient-to-br from-teal-50 to-white rounded-xl p-6"
                  data-testid="active-day-content"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-full bg-teal-600 flex items-center justify-center text-white font-bold">
                      {pkg.itinerary[activeDay].day}
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-slate-900">{pkg.itinerary[activeDay].title}</h3>
                      <p className="text-sm text-slate-500">Day {pkg.itinerary[activeDay].day}</p>
                    </div>
                  </div>
                  <p className="text-slate-600 leading-relaxed">{pkg.itinerary[activeDay].description}</p>
                </motion.div>
              )}
            </motion.section>

            {/* Inclusions & Exclusions */}
            <motion.section
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="grid grid-cols-1 md:grid-cols-2 gap-6"
              data-testid="inclusions-section"
            >
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <Check className="w-5 h-5 text-green-500" />
                  Inclusions
                </h3>
                <ul className="space-y-3">
                  {pkg.inclusions?.map((item, index) => (
                    <li key={index} className="flex items-start gap-3 text-slate-600">
                      <Check className="w-4 h-4 text-green-500 mt-1 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <X className="w-5 h-5 text-red-500" />
                  Exclusions
                </h3>
                <ul className="space-y-3">
                  {pkg.exclusions?.map((item, index) => (
                    <li key={index} className="flex items-start gap-3 text-slate-600">
                      <X className="w-4 h-4 text-red-500 mt-1 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.section>
          </div>

          {/* Sidebar - Booking Card */}
          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-2xl p-6 shadow-lg sticky top-24"
              data-testid="booking-card"
            >
              <div className="mb-6">
                <span className="text-sm text-slate-400 line-through">₹{pkg.original_price?.toLocaleString()}</span>
                <div className="flex items-baseline gap-2">
                  <span className="text-4xl font-bold text-teal-700">₹{pkg.price?.toLocaleString()}</span>
                  <span className="text-slate-500">per person</span>
                </div>
                <span className="inline-block mt-2 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                  Save ₹{(pkg.original_price - pkg.price)?.toLocaleString()}
                </span>
              </div>

              <div className="space-y-4 mb-6">
                <div className="flex items-center justify-between py-3 border-b border-slate-100">
                  <span className="text-slate-500">Duration</span>
                  <span className="font-medium">{pkg.duration}</span>
                </div>
                <div className="flex items-center justify-between py-3 border-b border-slate-100">
                  <span className="text-slate-500">Destination</span>
                  <span className="font-medium">{pkg.destination}</span>
                </div>
                <div className="flex items-center justify-between py-3 border-b border-slate-100">
                  <span className="text-slate-500">Hotels</span>
                  <span className="font-medium">{pkg.hotels}</span>
                </div>
              </div>

              <Button 
                onClick={() => setShowBookingModal(true)}
                disabled={bookingLoading}
                className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-xl py-6 text-lg font-medium mb-4"
                data-testid="book-now-btn"
              >
                {bookingLoading ? 'Processing...' : 'Book Now'}
              </Button>

              <a 
                href="tel:+919281410305"
                className="flex items-center justify-center gap-2 w-full py-3 border border-slate-200 rounded-xl text-slate-700 hover:bg-slate-50 transition-colors"
                data-testid="call-btn"
              >
                <Phone className="w-5 h-5" />
                Call for Enquiry
              </a>

              <p className="text-center text-sm text-slate-500 mt-4">
                Need help? Contact us 24/7
              </p>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Booking Modal */}
      <AnimatePresence>
        {showBookingModal && (
          <BookingModal
            isOpen={showBookingModal}
            onClose={() => setShowBookingModal(false)}
            onSubmit={handleBookingSubmit}
            title={`Book ${pkg?.name}`}
            loading={bookingLoading}
          />
        )}
      </AnimatePresence>

      {/* Booking Confirmation Modal */}
      <AnimatePresence>
        {showConfirmation && (
          <BookingConfirmation
            bookingType="package"
            bookingDetails={bookingDetails}
            onClose={() => setShowConfirmation(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
